'use client'

import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Input,
  LoadingDots,
} from '@/components/ui'

interface VoidOrderProps extends React.ComponentPropsWithoutRef<'div'> {
  orderId: string
  callback?: () => void
}

export const VoidOrder = ({ orderId, callback }: VoidOrderProps) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const queryClient = useQueryClient()
  const [isVoiding, setIsVoiding] = React.useState(false)
  const [reason, setReason] = React.useState('')

  const handleVoid = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsVoiding(true)

    try {
      const response = await fetch('/api/order/void', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: { orderId, reason } }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: response.statusText || 'Failed to void order',
        }))
        throw new Error(error?.message ?? 'Failed to void order')
      }

      toast.success('Order voided successfully')
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      setIsOpen(false)
      callback?.()
    } catch (error: any) {
      toast.error(error.message ?? 'Failed to void order')
      setIsOpen(false)
    } finally {
      setIsVoiding(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          className={cn('hidden gap-2 shadow-card lg:inline-flex')}
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            setIsOpen(true)
          }}
        >
          <span>Void Order</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Void Order?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to void the selected order. This action cannot be
            undone. Are you sure you want to proceed?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex flex-col gap-2">
          <Input
            type="text"
            placeholder="Reason"
            value={reason}
            onChangeValue={(value) => setReason(value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="w-full">Close</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleVoid}
            className="w-full"
            autoFocus
            disabled={isVoiding}
          >
            {isVoiding ? <LoadingDots className="h-4 w-4" /> : 'Void Order'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
